(() => {
  'use strict';
  const counts = [3, 4, 6];

  function init(page) {
    if (page.dataset.kafInitialized) return;
    const builder = page.querySelector('[data-kaf-builder]');
    if (!builder) return;
    let variants;
    try { variants = JSON.parse(page.querySelector('[data-kaf-variants]').textContent); }
    catch (_) { return; } // Keep the native product form when enhancement is unavailable.
    variants = variants.filter(v => Number.isSafeInteger(v.id) && Number.isFinite(v.price));
    if (!variants.length) return;
    const byId = new Map(variants.map(v => [String(v.id), v]));
    const defaultId = byId.has(page.dataset.defaultVariant) ? page.dataset.defaultVariant : String(variants[0].id);
    let selected = Array(4).fill(defaultId);
    let busy = false;
    const slots = builder.querySelector('[data-frame-slots]');
    const total = builder.querySelector('[data-total]');
    const itemLabel = builder.querySelector('[data-item-label]');
    const add = builder.querySelector('[data-add-gallery]');
    const status = builder.querySelector('[data-cart-status]');
    const help = builder.querySelector('[data-cart-help]');
    let formatter;
    try { formatter = new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: page.dataset.currency }); }
    catch (_) { return; }
    const money = cents => formatter.format(cents / 100);

    function items() {
      const grouped = new Map();
      selected.forEach(id => grouped.set(id, (grouped.get(id) || 0) + 1));
      return Array.from(grouped, ([id, quantity]) => ({ id: Number(id), quantity }));
    }
    function selectionError() {
      for (const item of items()) {
        const variant = byId.get(String(item.id));
        if (!variant || !variant.available) return 'Choose an available finish for every frame.';
        const rule = variant.quantity_rule || {};
        const min = rule.min || 1, increment = rule.increment || 1;
        if (item.quantity < min || (rule.max && item.quantity > rule.max) || item.quantity % increment !== 0) {
          return `Check the quantity for ${variant.title}. Minimum ${min}, in steps of ${increment}${rule.max ? `, maximum ${rule.max}` : ''}.`;
        }
      }
      return '';
    }
    function setStatus(message, error = false) {
      status.textContent = message;
      status.classList.toggle('is-error', error);
    }
    function update() {
      const subtotal = selected.reduce((sum, id) => sum + byId.get(id).price, 0);
      total.textContent = money(subtotal);
      itemLabel.textContent = `${selected.length} frames`;
      page.querySelectorAll('[data-count]').forEach(button => {
        button.setAttribute('aria-pressed', String(Number(button.dataset.count) === selected.length));
        button.disabled = busy;
      });
      page.querySelectorAll('[data-choose]').forEach(button => { button.disabled = busy; });
      slots.querySelectorAll('select').forEach(select => { select.disabled = busy; });
      const error = selectionError();
      add.disabled = busy || Boolean(error);
      if (!busy) setStatus(error, Boolean(error));
    }
    function renderSlots() {
      slots.replaceChildren();
      selected.forEach((id, index) => {
        const row = document.createElement('label');
        row.className = 'frame-slot';
        const number = document.createElement('span');
        number.className = 'slot-number';
        number.textContent = String(index + 1).padStart(2, '0');
        number.setAttribute('aria-hidden', 'true');
        const select = document.createElement('select');
        select.id = `KafFinish-${page.dataset.sectionId}-${index}`;
        select.dataset.slot = String(index);
        select.setAttribute('aria-label', `Frame ${index + 1} finish`);
        variants.forEach(variant => {
          const option = document.createElement('option');
          option.value = String(variant.id);
          option.textContent = `${variant.title} — ${money(variant.price)}${variant.available ? '' : ' — Sold out'}`;
          option.disabled = !variant.available;
          option.selected = String(variant.id) === id;
          select.append(option);
        });
        row.append(number, select);
        slots.append(row);
      });
    }
    function choose(value, scroll = false) {
      if (busy || !counts.includes(Number(value))) return;
      const count = Number(value);
      while (selected.length < count) selected.push(selected[selected.length - 1] || defaultId);
      selected = selected.slice(0, count);
      help.hidden = true;
      renderSlots(); update();
      if (scroll) page.querySelector(`[id="${page.dataset.sectionId}-gallery"]`).scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }
    page.addEventListener('click', event => {
      const button = event.target.closest('[data-count],[data-choose]');
      if (button && page.contains(button)) choose(button.dataset.count || button.dataset.choose, Boolean(button.dataset.choose));
    });
    slots.addEventListener('change', event => {
      const select = event.target.closest('select[data-slot]');
      if (!select || busy || !byId.has(select.value)) return;
      selected[Number(select.dataset.slot)] = select.value;
      help.hidden = true; update();
    });
    add.addEventListener('click', async () => {
      if (busy || selectionError()) return;
      busy = true; update();
      add.setAttribute('aria-busy', 'true');
      setStatus('Adding your gallery…'); help.hidden = true;
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 25000);
      try {
        const root = page.dataset.root.endsWith('/') ? page.dataset.root : `${page.dataset.root}/`;
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items() }), signal: controller.signal
        });
        const result = await response.json();
        if (!response.ok) {
          const message = typeof result.description === 'string' ? result.description : 'A selected frame may no longer be available.';
          throw new Error(`${message} Check your cart before trying again.`);
        }
        setStatus('Your frames are in the cart. Opening your cart…');
        window.location.assign(page.dataset.cart);
      } catch (error) {
        busy = false; update();
        const message = error instanceof TypeError || error.name === 'AbortError' ? 'We could not confirm the cart update. Check your cart before trying again.' : error.message;
        setStatus(message || 'We could not confirm the cart update. Please check your cart.', true);
        help.hidden = false;
        add.removeAttribute('aria-busy');
      } finally { clearTimeout(timeout); }
    });
    renderSlots(); update();
    page.classList.add('kaf-ready');
    page.dataset.kafInitialized = 'true';

    const sticky = page.querySelector('.sticky-buy');
    const hero = page.querySelector('.hero');
    const gallery = page.querySelector('.offer');
    const footer = document.querySelector('.shopify-section-group-footer-group');
    if (sticky && hero && gallery && 'IntersectionObserver' in window) {
      let heroVisible = true, galleryVisible = false, footerVisible = false;
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.target === hero) heroVisible = entry.isIntersecting;
          else if (entry.target === gallery) galleryVisible = entry.isIntersecting;
          else if (entry.target === footer) footerVisible = entry.isIntersecting;
        });
        sticky.classList.toggle('is-visible', !heroVisible && !galleryVisible && !footerVisible);
      });
      observer.observe(hero); observer.observe(gallery);
      if (footer) observer.observe(footer);
      page._kafObserver = observer;
    }
  }
  const initAll = () => document.querySelectorAll('[data-kaf-page]').forEach(init);
  initAll();
  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-kaf-page]').forEach(page => page._kafObserver?.disconnect()));
})();
